import React, { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography, Alert, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';
import axios from 'axios';
import Auth, { API_URL } from '../../../../utils/auth';
import { toast } from 'react-toastify';
import './ZoomManagement.css';

const ZoomManagement = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [useRange, setUseRange] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const workspaceId = Auth.getWorkspaceId();

  // Reusable styles for table cells
  const tableCellStyles = {
    label: { 
      fontWeight: 600, 
      borderBottom: 'none', 
      padding: '4px 8px', 
      verticalAlign: 'top' 
    },
    value: { 
      borderBottom: 'none', 
      padding: '4px 8px', 
      verticalAlign: 'top' 
    }
  };

  // Reusable styles for buttons
  const buttonStyles = {
    primary: {
      marginTop: 2,
      backgroundColor: '#6a5af9 !important',
      minWidth: '100px !important',
      height: '36px !important',
      '&:hover': {
        backgroundColor: '#5a4ae0 !important'
      }
    },
    cancel: {
      color: '#fff !important',
      backgroundColor: '#e74c3c !important',
      minWidth: '100px !important',
      height: '36px !important',
      '&:hover': {
        backgroundColor: '#c0392b !important'
      }
    },
    dateRange: {
      marginBottom: 1,
      color: '#6a5af9',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(106, 90, 249, 0.1)'
      }
    }
  };

  const handleProcessTranscripts = async () => {
    if (!workspaceId) {
      toast.error('Workspace ID not found');
      return;
    }

    setLoading(true);
    try {
      let targetDate = '';
      
      if (selectedDate) {
        const fromDateStr = selectedDate.format('YYYY-MM-DD');
        if (useRange && toDate) {
          const toDateStr = toDate.format('YYYY-MM-DD');
          targetDate = `${fromDateStr}&to=${toDateStr}`;
        } else {
          targetDate = fromDateStr;
        }
      } else {
        // Use last 24 hours if no date selected
        const yesterday = dayjs().subtract(1, 'day');
        const today = dayjs();
        targetDate = `${yesterday.format('YYYY-MM-DD')}&to=${today.format('YYYY-MM-DD')}`;
      }

      const response = await axios.post(
        `${API_URL}/zoom/zoom-process-transcripts`,
        {
          workspaceId: parseInt(workspaceId),
          targetDate
        }
      );

      // Check if transcripts were found
      if (!response.data.clientMeetings || response.data.clientMeetings.length === 0) {
        const dateRange = formatDateForDisplay(response.data.date || targetDate);
        toast.warning(`No transcripts found for the selected date or date range: ${dateRange}`);
      } else {
        toast.success('Transcript processing started successfully!');
      }
      
      setLastResult(response.data);
      setProcessDialogOpen(false);
      
    } catch (error) {
      console.error('Error processing transcripts:', error);
      toast.error('Failed to process transcripts: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };



  const isDateDisabled = (date) => {
    const today = dayjs();
    return date.isAfter(today, 'day');
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return dateString;
    
    // Check if it contains the range format
    if (dateString.includes('&to=')) {
      const [fromDate, toDate] = dateString.split('&to=');
      return toDate;
    }
    
    return dateString;
  };

  return (
    <div className="zoom-management">
      <div className="zoom-management-header">
        <div className="header-top">
          <h2>Zoom Management</h2>
          <IconButton 
            onClick={onBack}
            className="close-icon-button"
            sx={{ 
              color: '#6a5af9',
              '&:hover': { 
                backgroundColor: 'rgba(106, 90, 249, 0.1)',
                color: '#5a4ae0'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <p>Manage your Zoom transcript processing and analysis</p>
      </div>

      <div className="zoom-management-content">
        {lastResult && (
          <Alert 
            severity={lastResult.clientMeetings && lastResult.clientMeetings.length > 0 ? "info" : "warning"} 
            sx={{ marginBottom: 3 }}
          >
            {lastResult.clientMeetings && lastResult.clientMeetings.length > 0 ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 2 }}>
                  <Typography variant="body2" sx={{ textAlign: 'center', marginBottom: 2, fontSize: '14px' }}>
                    Calls are being processed. You can close this window while the call analyses for the identified customers are generated and displayed.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '600px', margin: '0 auto' }}>
                  {lastResult.date && (
                    <Box sx={{ display: 'flex', width: '100%', marginBottom: 1, alignItems: 'flex-start' }}>
                      <Typography sx={{ fontWeight: 600, minWidth: '120px', fontSize: '14px' }}>Date:</Typography>
                      <Typography sx={{ fontSize: '14px', marginLeft: 1 }}>{formatDateForDisplay(lastResult.date)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', marginBottom: 1 }}>Client Meetings:</Typography>
                    <Box sx={{ 
                      width: '100%',
                      maxHeight: '120px', 
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#6a5af9',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#5a4ae0',
                      },
                    }}>
                      {lastResult.clientMeetings.map((meeting, index) => (
                        <Typography 
                          key={index} 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            marginBottom: index === lastResult.clientMeetings.length - 1 ? 0 : 1,
                            fontSize: '12px',
                            lineHeight: 1.4,
                            color: '#333',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {meeting}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '600px', margin: '0 auto', gap: 2 }}>
                <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 500, fontSize: '14px' }}>
                  No transcripts found for the selected date or date range
                </Typography>
                {lastResult.date && (
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', justifyContent: 'center' }}>
                    <Typography sx={{ fontWeight: 600, minWidth: '120px', fontSize: '14px' }}>Date:</Typography>
                    <Typography sx={{ fontSize: '14px', marginLeft: 1 }}>{formatDateForDisplay(lastResult.date)}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Alert>
        )}

        {!lastResult && (
          <div className="management-options">
            <div className="option-card centered">
              <h3>Process Transcripts</h3>
              <p>Process Zoom transcripts for a specific date or date range</p>
              <Button
                variant="contained"
                className="process-button"
                onClick={() => setProcessDialogOpen(true)}
                disabled={loading}
                sx={buttonStyles.primary}
              >
                Process Transcripts
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Process Transcripts Dialog */}
      <Dialog 
        open={processDialogOpen} 
        onClose={() => setProcessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Transcripts</DialogTitle>
        <DialogContent>
          <Box sx={{ padding: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              Select a date to process transcripts. If no date is selected, the last 24 hours will be processed.
            </Typography>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                shouldDisableDate={isDateDisabled}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { marginBottom: 2 }
                  }
                }}
              />
              
              <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                <Button
                  variant="text"
                  onClick={() => setUseRange(!useRange)}
                  sx={buttonStyles.dateRange}
                  startIcon={<CalendarTodayIcon sx={{ color: '#6a5af9' }} />}
                >
                  {useRange ? 'Use Single Date' : 'Use Date Range'}
                </Button>
              </Box>

              {useRange && (
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  shouldDisableDate={isDateDisabled}
                  minDate={selectedDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { marginBottom: 2 }
                    }
                  }}
                />
              )}
            </LocalizationProvider>

            {selectedDate && (
              <Typography variant="caption" color="textSecondary">
                {useRange && toDate 
                  ? `Processing range: ${selectedDate.format('YYYY-MM-DD')} to ${toDate.format('YYYY-MM-DD')}`
                  : `Processing date: ${selectedDate.format('YYYY-MM-DD')}`
                }
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setProcessDialogOpen(false)}
            variant="contained"
            className="dialog-cancel-button"
            style={{
              backgroundColor: '#e74c3c',
              color: 'white',
              minWidth: '100px',
              height: '36px'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#c0392b'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#e74c3c'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleProcessTranscripts}
            variant="contained"
            disabled={loading}
            className="dialog-process-button"
            style={{
              backgroundColor: '#6a5af9',
              color: 'white',
              minWidth: '100px',
              height: '36px'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#5a4ae0')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#6a5af9')}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Process'}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default ZoomManagement;